import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { INTEGRATION_EMPTY_MESSAGES, CUSTOM_WEBHOOK_TEMPLATE_KEY } from "./integrationConstants";
import "../../starek.css";

export function IntegrationsEmptyState({ isFiltered, canAdd = true }) {
    const navigate = useNavigate();
    const [message] = useState(
        () => INTEGRATION_EMPTY_MESSAGES[Math.floor(Math.random() * INTEGRATION_EMPTY_MESSAGES.length)]
    );

    return (
        <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
            <div className="bg-empty-state-icon rounded-circle d-flex align-items-center justify-content-center mb-3 w-80px h-80px">
                <i className="fa fa-plug fa-2x text-primary" />
            </div>
            <h5 className="text-muted mb-1">
                {isFiltered ? "No Integrations Found" : message}
            </h5>
            <p className="text-muted small mb-3 max-width-320px">
                {isFiltered
                    ? "No integrations match your search. Try adjusting the filter."
                    : "No integrations have been installed yet."}
            </p>
            {!isFiltered && canAdd && (
                <button
                    className="btn btn-primary btn-sm"
                    onClick={() => navigate("/integrations/available")}
                >
                    <i className="fa fa-th me-1" />
                    View Available Integrations
                </button>
            )}
        </div>
    );
}

function IntegrationsCardView({
    items,
    selectedRows,
    onSelectedRowsChange,
    filterText,
    templatesByKey,
    togglingActiveId,
    onToggleActive,
    canAdd = true,
}) {
    const isSelected = (id) => selectedRows.some((row) => row.id === id);

    const handleCheckboxChange = (integration) => {
        const next = isSelected(integration.id)
            ? selectedRows.filter((row) => row.id !== integration.id)
            : [...selectedRows, integration];
        if (onSelectedRowsChange) onSelectedRowsChange({ selectedRows: next });
    };

    const filteredItems = useMemo(() => {
        const q = filterText?.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [items, filterText]);

    if (filteredItems.length === 0) {
        return <IntegrationsEmptyState isFiltered={!!filterText?.trim()} canAdd={canAdd} />;
    }

    return (
        <div className="row g-3">
            {filteredItems.map((integration) => {
                const template = templatesByKey?.[integration.integration_key];
                const isCustom = integration.integration_key === CUSTOM_WEBHOOK_TEMPLATE_KEY || !template;

                return (
                    <div key={integration.id} className="col-lg-3 col-md-4 col-sm-6">
                        <div
                            className={`card h-100 shadow-sm cursor-pointer ${
                                isSelected(integration.id) ? "border-primary border-2" : ""
                            } ${integration.is_active === false ? "opacity-inactive-card" : ""}`}
                        >
                            <div className="card-header bg-primary text-white">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                        <div className="form-check mb-0 me-2 flex-shrink-0">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={isSelected(integration.id)}
                                                onChange={() => handleCheckboxChange(integration)}
                                                aria-label={`Select ${integration.name}`}
                                            />
                                        </div>
                                        <h5
                                            className="card-title mb-0 text-truncate"
                                            title={integration.name}
                                        >
                                            {integration.name}
                                        </h5>
                                    </div>
                                    <div className="d-flex align-items-center gap-1 flex-shrink-0 ms-2" onClick={(e) => e.stopPropagation()}>
                                        {onToggleActive && (
                                            <button
                                                className={`header-toggle-btn btn btn-sm ${
                                                    integration.is_active ? "btn-outline-light" : "btn-outline-warning"
                                                }`}
                                                title={integration.is_active ? "Deactivate" : "Restore"}
                                                disabled={togglingActiveId === integration.id}
                                                onClick={() => onToggleActive(integration)}
                                            >
                                                {togglingActiveId === integration.id ? (
                                                    <span className="spinner-border spinner-border-sm" />
                                                ) : (
                                                    <i className={`fa ${integration.is_active ? "fa-ban" : "fa-undo"}`} />
                                                )}
                                            </button>
                                        )}
                                        {template?.icon ? (
                                            <img
                                                src={template.icon}
                                                alt=""
                                                className="w-22px h-22px object-fit-contain"
                                                onError={(e) => { e.target.style.display = "none"; }}
                                            />
                                        ) : (
                                            <i className="fa fa-plug fa-lg"></i>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex gap-1 flex-wrap mb-2">
                                    {isCustom ? (
                                        <span className="badge bg-secondary">Custom Webhook</span>
                                    ) : (
                                        <span className="badge bg-info text-white">{template.category}</span>
                                    )}
                                    <span className={`badge bg-${integration.is_active ? "success" : "secondary"}`}>
                                        {integration.is_active ? "Active" : "Inactive"}
                                    </span>
                                </div>
                                <p className="text-muted mb-0 flex-grow-1 font-size-14px">
                                    {integration.description || template?.description || "No description"}
                                </p>
                                <div className="border-top pt-2 mt-auto">
                                    <small className="text-muted d-block">
                                        <i className="fas fa-clock me-1"></i>
                                        {moment.utc(integration.created_at).local().format("MMM D, YYYY HH:mm")}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default IntegrationsCardView;
